/**
 * Joins this window to the live wire — the last hop of the real-time chain.
 *
 * The chain, end to end, when a מפקד taps a soldier's status on their phone:
 *
 * ```
 *   fleet-store.updateSoldier()
 *     └─ emitStoreChange('fleet')
 *          ├─ this window's screens re-read            (src/hooks/use-store-events.ts)
 *          ├─ the cloud push is signalled              (src/core/sync-signal.ts)
 *          └─ every other window on this device        ← this hook
 *                └─ their screens re-read on the same frame
 *
 *   …and on every other operator's phone:
 *     use-cloud-sync's pass pulls the row → applyPull writes it → onRemoteChange
 *       └─ the shell re-reads
 * ```
 *
 * Mounting this hook is what installs the middle branch. Without it the app
 * behaves exactly as it did — the poll still carries every change — so it is
 * additive in the same way the mirror itself is, and a browser with no
 * `BroadcastChannel` simply never gets the branch.
 */
import { useEffect, useRef } from 'react';
import { startLiveUpdates } from '../../core/live-updates';
import type { LiveUpdate } from '../../core/live-updates';

/**
 * @param enabled  Only a signed-in shell listens. A visitor at the sign-in
 *                 screen has no data on show and nothing to keep in step.
 * @param onUpdate Optional — called for each foreign announcement, after the
 *                 screens have already been told to re-read. The re-read is not
 *                 the caller's job; this is for anything extra a shell wants to
 *                 do with the news.
 */
export function useLiveUpdates(enabled: boolean, onUpdate?: (update: LiveUpdate) => void): void {
  // Held in a ref so a caller passing a fresh closure every render does not tear
  // the channel down and build it again every render.
  const latest = useRef(onUpdate);
  latest.current = onUpdate;

  useEffect(() => {
    if (!enabled) return;
    return startLiveUpdates((update) => latest.current?.(update));
  }, [enabled]);
}
